import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { 
  FileText, 
  Plus, 
  Eye, 
  Edit,
  Trash2,
  Copy,
  ExternalLink,
  ArrowLeft,
  DollarSign,
  MapPin,
  User,
  Calendar,
  Camera,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import TourOperatorSelect from './TourOperatorSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteManager = () => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);

  // Auto-prefill from Travel Radar (querystring ?from_radar=1&title=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from_radar') !== '1') return;
    const title = params.get('title') || '';
    const destination = params.get('destination') || '';
    const price = params.get('price');
    const nights = params.get('nights');
    const departure = params.get('departure') || '';
    setQuoteForm((prev) => ({
      ...prev,
      title: title || prev.title,
      destination: destination
        ? { ...prev.destination, city: destination }
        : prev.destination,
      start_date: departure ? departure.slice(0, 10) : prev.start_date,
      nights: nights ? parseInt(nights, 10) : prev.nights,
      introduction: price
        ? `Offerta dal Travel Radar — prezzo di partenza ${parseFloat(price).toFixed(2)} €.\n${prev.introduction || ''}`
        : prev.introduction,
    }));
    setShowCreateDialog(true);
    toast.success(`Preventivo pre-compilato dal Radar: ${title}`);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // Form state for creating/editing quotes - Updated with travel type and itinerary
  const [quoteForm, setQuoteForm] = useState({
    title: '',
    travel_type: 'Mare Italia',
    start_date: '',  // Data inizio viaggio
    end_date: '',    // Data fine viaggio
    nights: 0,       // Numero di notti
    itinerary: {
      days: [],
      total_days: 0,
      notes: ''
    },
    client_registry: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      nationality: 'Italiana',
      address: '',
      city: '',
      postal_code: '',
      tax_code: '',
      participants: {
        adults: 2,
        children: 0,
        children_ages: []
      }
    },
    destination: {
      country: '',
      city: '',
      flag_emoji: '',
      climate: '',
      best_period: '',
      attractions: '',
      typical_food: ''
    },
    services: [],
    contacts: {
      agent_name: user?.first_name + ' ' + user?.last_name || '',
      agent_email: user?.email || '',
      agent_whatsapp: '',
      agency_name: 'Tribù in Viaggio'
    },
    agent_logo_url: user?.logo_url || '',
    discount_amount: 0,  // Changed from discount_percentage to discount_amount (Euro)
    validity_date: '',  // Quote expiry date
    tour_operator_id: null,
    gallery: {
      images: []
    },
    // Quote Content Sections
    introduction: '',
    travel_context: '',
    choice_explanation: '',
    client_benefits: '',
    ideal_for: ''
  });

  // Participant form state - Simplified
  const [uploadingImages, setUploadingImages] = useState(false);
  
  // Itinerary management state
  const [newItineraryDay, setNewItineraryDay] = useState({
    day_number: 1,
    location: '',
    activities: '',
    meals: '',
    accommodation: ''
  });

  // Check if travel type requires itinerary
  const requiresItinerary = (travelType) => {
    return travelType === 'Crociera' || travelType === 'Gruppi Organizzati';
  };

  // Itinerary management functions
  const addItineraryDay = () => {
    if (!newItineraryDay.location || !newItineraryDay.activities) {
      toast.error('Inserisci luogo e attività del giorno');
      return;
    }

    const day = { ...newItineraryDay };
    const updatedDays = [...quoteForm.itinerary.days, day];
    
    setQuoteForm({
      ...quoteForm,
      itinerary: {
        ...quoteForm.itinerary,
        days: updatedDays,
        total_days: updatedDays.length
      }
    });

    setNewItineraryDay({
      day_number: updatedDays.length + 1,
      location: '',
      activities: '',
      meals: '',
      accommodation: ''
    });
  };

  const removeItineraryDay = (index) => {
    const updatedDays = quoteForm.itinerary.days.filter((_, i) => i !== index);
    // Renumber days
    const renumberedDays = updatedDays.map((day, i) => ({
      ...day,
      day_number: i + 1
    }));
    
    setQuoteForm({
      ...quoteForm,
      itinerary: {
        ...quoteForm.itinerary,
        days: renumberedDays,
        total_days: renumberedDays.length
      }
    });
    
    // Update new day number
    setNewItineraryDay({
      ...newItineraryDay,
      day_number: renumberedDays.length + 1
    });
  };
  const updateChildrenAges = (childrenCount) => {
    const currentAges = quoteForm.client_registry.participants.children_ages;
    let newAges = [];
    
    for (let i = 0; i < childrenCount; i++) {
      if (i < currentAges.length) {
        newAges.push(currentAges[i]); // Keep existing age
      } else {
        newAges.push(5); // Default age for new children
      }
    }
    
    setQuoteForm({
      ...quoteForm,
      client_registry: {
        ...quoteForm.client_registry,
        participants: {
          ...quoteForm.client_registry.participants,
          children: childrenCount,
          children_ages: newAges
        }
      }
    });
  };

  const updateChildAge = (index, age) => {
    const newAges = [...quoteForm.client_registry.participants.children_ages];
    newAges[index] = parseInt(age);
    
    setQuoteForm({
      ...quoteForm,
      client_registry: {
        ...quoteForm.client_registry,
        participants: {
          ...quoteForm.client_registry.participants,
          children_ages: newAges
        }
      }
    });
  };

  // Service form state
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    price: '',
    is_included: true,
    category: 'transport'
  });

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async () => {
    try {
      if (!quoteForm.title || !quoteForm.client_registry.first_name || !quoteForm.client_registry.last_name || 
          !quoteForm.client_registry.email || !quoteForm.destination.country || !quoteForm.destination.city) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      // Prepare form data with proper date handling
      const formDataToSend = {
        ...quoteForm,
        client_registry: {
          ...quoteForm.client_registry,
          date_of_birth: quoteForm.client_registry.date_of_birth || null  // Send null instead of empty string
        },
        validity_date: quoteForm.validity_date ? new Date(quoteForm.validity_date).toISOString() : null,
        // Only include itinerary if travel type requires it and has days
        itinerary: requiresItinerary(quoteForm.travel_type) && quoteForm.itinerary.days.length > 0 
          ? quoteForm.itinerary 
          : null
      };

      const response = await axios.post(`${API}/quotes`, formDataToSend);
      toast.success('Preventivo creato con successo!');
      setShowCreateDialog(false);
      resetForm();
      fetchQuotes();
      
      // Show public URL
      const publicUrl = `${window.location.origin}/preventivo/${response.data.quote.public_url_slug}`;
      toast.success(`URL pubblico: ${publicUrl}`, { duration: 5000 });
      
    } catch (error) {
      console.error('Error creating quote:', error);
      let errorMessage = 'Errore nella creazione del preventivo';
      
      if (error.response?.data?.detail) {
        // Handle different types of detail responses
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors array
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc ? err.loc.join('.') : 'Campo'}: ${err.msg || 'Errore di validazione'}`
          );
          errorMessage = `Errori di validazione:\n${validationErrors.join('\n')}`;
        } else {
          errorMessage = 'Errore di validazione dati';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUpdateQuote = async () => {
    try {
      // Prepare form data with proper date handling  
      const formDataToSend = {
        ...quoteForm,
        client_registry: {
          ...quoteForm.client_registry,
          date_of_birth: quoteForm.client_registry.date_of_birth || null  // Send null instead of empty string
        },
        validity_date: quoteForm.validity_date ? new Date(quoteForm.validity_date).toISOString() : null,
        // Only include itinerary if travel type requires it and has days
        itinerary: requiresItinerary(quoteForm.travel_type) && quoteForm.itinerary.days.length > 0 
          ? quoteForm.itinerary 
          : null
      };

      await axios.put(`${API}/quotes/${editingQuote.id}`, formDataToSend);
      toast.success('Preventivo aggiornato con successo!');
      setShowEditDialog(false);
      setEditingQuote(null);
      resetForm();
      fetchQuotes();
    } catch (error) {
      console.error('Error updating quote:', error);
      let errorMessage = 'Errore nell\'aggiornamento del preventivo';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc ? err.loc.join('.') : 'Campo'}: ${err.msg || 'Errore di validazione'}`
          );
          errorMessage = `Errori di validazione:\n${validationErrors.join('\n')}`;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo preventivo?')) {
      try {
        await axios.delete(`${API}/quotes/${quoteId}`);
        toast.success('Preventivo eliminato con successo!');
        fetchQuotes();
      } catch (error) {
        console.error('Error deleting quote:', error);
        toast.error('Errore nell\'eliminazione del preventivo');
      }
    }
  };

  const handleCopyUrl = (quote) => {
    const publicUrl = `${window.location.origin}/preventivo/${quote.public_url_slug}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      toast.success('URL copiato negli appunti!');
    });
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setQuoteForm({
      title: quote.title,
      travel_type: quote.travel_type || 'Mare Italia',
      start_date: quote.start_date || '',
      end_date: quote.end_date || '',
      nights: quote.nights || 0,
      itinerary: quote.itinerary || {
        days: [],
        total_days: 0,
        notes: ''
      },
      client_registry: quote.client_registry || {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        nationality: 'Italiana',
        address: '',
        city: '',
        postal_code: '',
        tax_code: '',
        participants: {
          adults: 2,
          children: 0,
          children_ages: []
        }
      },
      destination: quote.destination,
      services: quote.services,
      contacts: quote.contacts,
      agent_logo_url: quote.agent_logo_url || '',
      discount_amount: quote.discount_amount || 0,  // Changed from discount_percentage
      validity_date: quote.validity_date || '',  // Quote expiry date
      tour_operator_id: quote.tour_operator_id || null,
      gallery: quote.gallery || { images: [] },
      // Quote Content Sections
      introduction: quote.introduction || '',
      travel_context: quote.travel_context || '',
      choice_explanation: quote.choice_explanation || '',
      client_benefits: quote.client_benefits || '',
      ideal_for: quote.ideal_for || ''
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setQuoteForm({
      title: '',
      travel_type: 'Mare Italia',
      start_date: '',
      end_date: '',
      nights: 0,
      itinerary: {
        days: [],
        total_days: 0,
        notes: ''
      },
      client_registry: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        nationality: 'Italiana',
        address: '',
        city: '',
        postal_code: '',
        tax_code: '',
        participants: {
          adults: 2,
          children: 0,
          children_ages: []
        }
      },
      destination: {
        country: '',
        city: '',
        flag_emoji: '',
        climate: '',
        best_period: '',
        attractions: '',
        typical_food: ''
      },
      services: [],
      contacts: {
        agent_name: user?.first_name + ' ' + user?.last_name || '',
        agent_email: user?.email || '',
        agent_whatsapp: '',
        agency_name: 'Tribù in Viaggio'
      },
      agent_logo_url: user?.logo_url || '',
      discount_amount: 0,  // Changed from discount_percentage
      validity_date: '',  // Quote expiry date
      tour_operator_id: null,
      gallery: {
        images: []
      },
      // Quote Content Sections
      introduction: '',
      travel_context: '',
      choice_explanation: '',
      client_benefits: '',
      ideal_for: ''
    });
    setNewService({
      name: '',
      description: '',
      price: '',
      is_included: true,
      category: 'transport'
    });
    setNewItineraryDay({
      day_number: 1,
      location: '',
      activities: '',
      meals: '',
      accommodation: ''
    });
  };

  const addService = () => {
    if (!newService.name || !newService.price) {
      toast.error('Inserisci nome e prezzo del servizio');
      return;
    }

    const service = {
      ...newService,
      price: parseFloat(newService.price)
    };

    setQuoteForm({
      ...quoteForm,
      services: [...quoteForm.services, service]
    });

    setNewService({
      name: '',
      description: '',
      price: '',
      is_included: true,
      category: 'transport'
    });
  };

  const removeService = (index) => {
    const updatedServices = quoteForm.services.filter((_, i) => i !== index);
    setQuoteForm({
      ...quoteForm,
      services: updatedServices
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const calculatePricing = () => {
    const basePrice = quoteForm.services
      .filter(service => service.is_included)
      .reduce((sum, service) => sum + service.price, 0);
    
    const supplementsPrice = quoteForm.services
      .filter(service => !service.is_included)
      .reduce((sum, service) => sum + service.price, 0);
    
    const totalPrice = basePrice + supplementsPrice;
    const discountAmount = quoteForm.discount_amount || 0;  // Direct Euro amount
    const finalPrice = Math.max(0, totalPrice - discountAmount);  // Ensure non-negative

    return {
      basePrice,
      supplementsPrice,
      totalPrice,
      discountAmount,
      finalPrice
    };
  };

  // Image upload handlers
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Check if adding these files would exceed the limit
    const currentImages = quoteForm.gallery?.images || [];
    if (currentImages.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} non è un'immagine valida`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB
        toast.error(`${file.name} è troppo grande (max 10MB)`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setUploadingImages(true);
    
    try {
      const uploadPromises = validFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        return response.data.file_path;
      });

      const uploadedPaths = await Promise.all(uploadPromises);
      
      setQuoteForm({
        ...quoteForm,
        gallery: {
          ...quoteForm.gallery,
          images: [...currentImages, ...uploadedPaths]
        }
      });

      toast.success(`${uploadedPaths.length} immagini caricate con successo!`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Errore nel caricamento delle immagini');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    const currentImages = quoteForm.gallery?.images || [];
    const updatedImages = currentImages.filter((_, i) => i !== index);
    
    setQuoteForm({
      ...quoteForm,
      gallery: {
        ...quoteForm.gallery,
        images: updatedImages
      }
    });
    
    toast.success('Immagine rimossa');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const pricing = calculatePricing();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center h-auto sm:h-16 py-4 sm:py-0">
            <div className="flex items-center mb-4 sm:mb-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="mr-4">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <img 
                src="https://customer-assets.emergentagent.com/job_e899d787-232a-49fb-887e-eb0377fd3380/artifacts/dy8a9fcf_trib%C3%B9%20in%20viaggio.png" 
                alt="Tribù in Viaggio" 
                className="h-10 sm:h-12 w-auto mr-3"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">Gestione Preventivi</h1>
                <p className="text-sm text-slate-600">Crea e gestisci preventivi professionali</p>
              </div>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2 w-full sm:w-auto">
                  <Plus className="w-4 h-4" />
                  <span className="sm:inline">Nuovo Preventivo</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Crea Nuovo Preventivo</DialogTitle>
                  <DialogDescription>
                    Compila tutti i campi per creare un preventivo professionale
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Basic Info - Updated for travel type and validity */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Titolo Preventivo *</Label>
                      <Input
                        id="title"
                        value={quoteForm.title}
                        onChange={(e) => setQuoteForm({...quoteForm, title: e.target.value})}
                        placeholder="es. Vacanza Costiera Amalfitana"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="discount_amount">Sconto (€)</Label>
                      <Input
                        id="discount_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={quoteForm.discount_amount}
                        onChange={(e) => setQuoteForm({...quoteForm, discount_amount: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="validity_date">Data Validità Preventivo</Label>
                      <Input
                        id="validity_date"
                        type="date"
                        value={quoteForm.validity_date}
                        onChange={(e) => setQuoteForm({...quoteForm, validity_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-slate-500">
                        Fino a quando il preventivo è valido
                      </p>
                    </div>
                  </div>

                  {/* Tour Operator from Rubrica (optional link) */}
                  <TourOperatorSelect
                    value={quoteForm.tour_operator_id}
                    onChange={(id) => setQuoteForm({...quoteForm, tour_operator_id: id})}
                    label="Tour Operator collegato (dalla Rubrica T.O.) — opzionale"
                    placeholder="Collega un T.O. al preventivo (facoltativo)"
                    testId="quote-tour-operator-select"
                  />

                  {/* Travel Type Selector */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Tipo di Viaggio / Pacchetto
                      </CardTitle>
                      <CardDescription>
                        Seleziona la tipologia di viaggio per personalizzare l'offerta
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label htmlFor="travel_type">Tipo di Pacchetto *</Label>
                        <Select 
                          value={quoteForm.travel_type} 
                          onValueChange={(value) => {
                            setQuoteForm({
                              ...quoteForm, 
                              travel_type: value,
                              // Reset itinerary if changing to/from types that don't require it
                              itinerary: requiresItinerary(value) ? quoteForm.itinerary : {
                                days: [],
                                total_days: 0,
                                notes: ''
                              }
                            });
                            // Reset new itinerary day
                            setNewItineraryDay({
                              day_number: 1,
                              location: '',
                              activities: '',
                              meals: '',
                              accommodation: ''
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona tipo di viaggio" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Crociera">🚢 Crociera</SelectItem>
                            <SelectItem value="Mare Italia">🏖️ Mare Italia</SelectItem>
                            <SelectItem value="Montagna inverno">⛷️ Montagna Inverno</SelectItem>
                            <SelectItem value="Montagna estate">🏔️ Montagna Estate</SelectItem>
                            <SelectItem value="Medio Raggio">✈️ Medio Raggio</SelectItem>
                            <SelectItem value="Lungo Raggio">🌍 Lungo Raggio</SelectItem>
                            <SelectItem value="Gruppi Organizzati">👥 Gruppi Organizzati</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <strong>Tipo selezionato:</strong> {quoteForm.travel_type}
                          {requiresItinerary(quoteForm.travel_type) && (
                            <span className="ml-2 text-blue-600">• Include sezione itinerario</span>
                          )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Travel Dates Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Date del Viaggio
                      </CardTitle>
                      <CardDescription>
                        Periodo e durata del viaggio
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="start_date">Data Partenza *</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={quoteForm.start_date}
                            onChange={(e) => {
                              const startDate = e.target.value;
                              const endDate = quoteForm.end_date;
                              let nights = quoteForm.nights;
                              
                              // Auto-calculate nights if both dates are set
                              if (startDate && endDate) {
                                const start = new Date(startDate);
                                const end = new Date(endDate);
                                const diffTime = end - start;
                                nights = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
                              }
                              
                              setQuoteForm({
                                ...quoteForm,
                                start_date: startDate,
                                nights: nights
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="end_date">Data Rientro *</Label>
                          <Input
                            id="end_date"
                            type="date"
                            value={quoteForm.end_date}
                            min={quoteForm.start_date}
                            onChange={(e) => {
                              const endDate = e.target.value;
                              const startDate = quoteForm.start_date;
                              let nights = quoteForm.nights;
                              
                              // Auto-calculate nights if both dates are set
                              if (startDate && endDate) {
                                const start = new Date(startDate);
                                const end = new Date(endDate);
                                const diffTime = end - start;
                                nights = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
                              }
                              
                              setQuoteForm({
                                ...quoteForm,
                                end_date: endDate,
                                nights: nights
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nights">Notti</Label>
                          <Input
                            id="nights"
                            type="number"
                            min="0"
                            value={quoteForm.nights}
                            onChange={(e) => setQuoteForm({...quoteForm, nights: parseInt(e.target.value) || 0})}
                            className="bg-slate-50"
                          />
                          <p className="text-xs text-slate-500">Calcolato automaticamente</p>
                        </div>
                      </div>
                      
                      {/* Summary */}
                      {quoteForm.start_date && quoteForm.end_date && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-700">
                            📅 <strong>Periodo:</strong> {new Date(quoteForm.start_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })} - {new Date(quoteForm.end_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                            <span className="ml-3">🌙 <strong>{quoteForm.nights}</strong> notti</span>
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Itinerary Section - Conditional */}
                  {requiresItinerary(quoteForm.travel_type) && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Itinerario di Viaggio
                        </CardTitle>
                        <CardDescription>
                          Programma dettagliato per {quoteForm.travel_type}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Add Day Form */}
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 space-y-4">
                          <h4 className="font-medium">Aggiungi Giorno all'Itinerario</h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="day_location">Località *</Label>
                              <Input
                                id="day_location"
                                value={newItineraryDay.location}
                                onChange={(e) => setNewItineraryDay({...newItineraryDay, location: e.target.value})}
                                placeholder="es. Roma, Barcellona, Santorini"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="day_accommodation">Sistemazione</Label>
                              <Input
                                id="day_accommodation"
                                value={newItineraryDay.accommodation}
                                onChange={(e) => setNewItineraryDay({...newItineraryDay, accommodation: e.target.value})}
                                placeholder="es. Hotel 4*, Nave, Pensione"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="day_activities">Attività Programmate *</Label>
                            <Textarea
                              id="day_activities"
                              value={newItineraryDay.activities}
                              onChange={(e) => setNewItineraryDay({...newItineraryDay, activities: e.target.value})}
                              placeholder="Descrivi le attività, visite, escursioni previste"
                              rows={3}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="day_meals">Pasti Inclusi</Label>
                            <Input
                              id="day_meals"
                              value={newItineraryDay.meals}
                              onChange={(e) => setNewItineraryDay({...newItineraryDay, meals: e.target.value})}
                              placeholder="es. Colazione, Pranzo, Cena"
                            />
                          </div>
                          
                          <Button onClick={addItineraryDay} className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            Aggiungi Giorno {newItineraryDay.day_number}
                          </Button>
                        </div>

                        {/* Itinerary Days List */}
                        <div className="space-y-3">
                          {quoteForm.itinerary.days.map((day, index) => (
                            <div key={index} className="p-4 border rounded-lg bg-white">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline">Giorno {day.day_number}</Badge>
                                    <h4 className="font-semibold">{day.location}</h4>
                                  </div>
                                  <div className="space-y-1 text-sm text-slate-600">
                                    <p><strong>Attività:</strong> {day.activities}</p>
                                    {day.accommodation && (
                                      <p><strong>Sistemazione:</strong> {day.accommodation}</p>
                                    )}
                                    {day.meals && (
                                      <p><strong>Pasti:</strong> {day.meals}</p>
                                    )}
                                  </div>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => removeItineraryDay(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          
                          {quoteForm.itinerary.days.length === 0 && (
                            <div className="text-center py-8 text-slate-500">
                              <Calendar className="mx-auto h-12 w-12 text-slate-300 mb-3" />
                              <p>Nessun giorno aggiunto all'itinerario</p>
                              <p className="text-sm">Aggiungi i giorni del programma di viaggio</p>
                            </div>
                          )}
                        </div>

                        {/* Itinerary Notes */}
                        <div className="space-y-2">
                          <Label htmlFor="itinerary_notes">Note Itinerario</Label>
                          <Textarea
                            id="itinerary_notes"
                            value={quoteForm.itinerary.notes}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              itinerary: {...quoteForm.itinerary, notes: e.target.value}
                            })}
                            placeholder="Note generali, informazioni importanti, variazioni possibili..."
                            rows={2}
                          />
                        </div>

                        {/* Itinerary Summary */}
                        {quoteForm.itinerary.days.length > 0 && (
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <h4 className="font-medium text-blue-800 mb-1">Riepilogo Itinerario</h4>
                            <p className="text-sm text-blue-700">
                              Programma di {quoteForm.itinerary.total_days} giorni • 
                              Località visitate: {quoteForm.itinerary.days.map(d => d.location).join(', ')}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Client Registry - Complete client information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Anagrafica Cliente
                      </CardTitle>
                      <CardDescription>
                        Informazioni complete del cliente intestatario
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_first_name">Nome *</Label>
                          <Input
                            id="client_first_name"
                            value={quoteForm.client_registry.first_name}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, first_name: e.target.value}
                            })}
                            placeholder="Mario"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_last_name">Cognome *</Label>
                          <Input
                            id="client_last_name"
                            value={quoteForm.client_registry.last_name}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, last_name: e.target.value}
                            })}
                            placeholder="Rossi"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_email">Email *</Label>
                          <Input
                            id="client_email"
                            type="email"
                            value={quoteForm.client_registry.email}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, email: e.target.value}
                            })}
                            placeholder="mario.rossi@email.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_phone">Telefono *</Label>
                          <Input
                            id="client_phone"
                            value={quoteForm.client_registry.phone}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, phone: e.target.value}
                            })}
                            placeholder="+39 333 123 4567"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_birth_date">Data di Nascita</Label>
                          <Input
                            id="client_birth_date"
                            type="date"
                            value={quoteForm.client_registry.date_of_birth}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, date_of_birth: e.target.value}
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_nationality">Nazionalità</Label>
                          <Input
                            id="client_nationality"
                            value={quoteForm.client_registry.nationality}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, nationality: e.target.value}
                            })}
                            placeholder="Italiana"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_tax_code">Codice Fiscale</Label>
                          <Input
                            id="client_tax_code"
                            value={quoteForm.client_registry.tax_code}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, tax_code: e.target.value}
                            })}
                            placeholder="RSSMRA80A01H501X"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client_address">Indirizzo Completo</Label>
                        <Textarea
                          id="client_address"
                          value={quoteForm.client_registry.address}
                          onChange={(e) => setQuoteForm({
                            ...quoteForm,
                            client_registry: {...quoteForm.client_registry, address: e.target.value}
                          })}
                          placeholder="Via Roma 123, 20100 Milano (MI)"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_city">Città</Label>
                          <Input
                            id="client_city"
                            value={quoteForm.client_registry.city}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, city: e.target.value}
                            })}
                            placeholder="Milano"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_postal_code">CAP</Label>
                          <Input
                            id="client_postal_code"
                            value={quoteForm.client_registry.postal_code}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, postal_code: e.target.value}
                            })}
                            placeholder="20100"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Participants Management - Simplified */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Partecipanti al Viaggio
                      </CardTitle>
                      <CardDescription>
                        Indica il numero di adulti e bambini
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="adults_count">Adulti (18+ anni)</Label>
                          <Input
                            id="adults_count"
                            type="number"
                            min="1"
                            max="10"
                            value={quoteForm.client_registry.participants.adults}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {
                                ...quoteForm.client_registry,
                                participants: {
                                  ...quoteForm.client_registry.participants,
                                  adults: parseInt(e.target.value) || 1
                                }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="children_count">Bambini (1-17 anni)</Label>
                          <Input
                            id="children_count"
                            type="number"
                            min="0"
                            max="6"
                            value={quoteForm.client_registry.participants.children}
                            onChange={(e) => updateChildrenAges(parseInt(e.target.value) || 0)}
                          />
                        </div>
                      </div>

                      {/* Children Ages */}
                      {quoteForm.client_registry.participants.children > 0 && (
                        <div className="space-y-3">
                          <Label>Età dei Bambini</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Array.from({ length: quoteForm.client_registry.participants.children }, (_, index) => (
                              <div key={index} className="space-y-1">
                                <Label className="text-sm">Bambino {index + 1}</Label>
                                <Select 
                                  value={String(quoteForm.client_registry.participants.children_ages[index] || 5)}
                                  onValueChange={(value) => updateChildAge(index, value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: 17 }, (_, i) => i + 1).map((age) => (
                                      <SelectItem key={age} value={String(age)}>
                                        {age} {age === 1 ? 'anno' : 'anni'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <h4 className="font-medium text-slate-800 mb-2">Riepilogo Partecipanti</h4>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span>{quoteForm.client_registry.participants.adults} Adulti</span>
                          {quoteForm.client_registry.participants.children > 0 && (
                            <span>
                              {quoteForm.client_registry.participants.children} Bambini
                              {quoteForm.client_registry.participants.children_ages.length > 0 && (
                                <span className="ml-1">
                                  (età: {quoteForm.client_registry.participants.children_ages.join(', ')})
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 text-sm font-medium text-slate-800">
                          Totale: {quoteForm.client_registry.participants.adults + quoteForm.client_registry.participants.children} persone
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Destination */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Informazioni Destinazione
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="country">Paese *</Label>
                          <Input
                            id="country"
                            value={quoteForm.destination.country}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, country: e.target.value}
                            })}
                            placeholder="Italia"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city">Città *</Label>
                          <Input
                            id="city"
                            value={quoteForm.destination.city}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, city: e.target.value}
                            })}
                            placeholder="Roma"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="flag_emoji">Bandiera Emoji</Label>
                          <Input
                            id="flag_emoji"
                            value={quoteForm.destination.flag_emoji}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, flag_emoji: e.target.value}
                            })}
                            placeholder="🇮🇹"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="climate">Clima</Label>
                          <Input
                            id="climate"
                            value={quoteForm.destination.climate}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, climate: e.target.value}
                            })}
                            placeholder="Mediterraneo"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="best_period">Periodo Migliore</Label>
                          <Input
                            id="best_period"
                            value={quoteForm.destination.best_period}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, best_period: e.target.value}
                            })}
                            placeholder="Maggio-Settembre"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="attractions">Attrazioni Principali</Label>
                        <Textarea
                          id="attractions"
                          value={quoteForm.destination.attractions}
                          onChange={(e) => setQuoteForm({
                            ...quoteForm,
                            destination: {...quoteForm.destination, attractions: e.target.value}
                          })}
                          placeholder="Colosseo, Fontana di Trevi, Musei Vaticani..."
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="typical_food">Cucina Tipica</Label>
                        <Textarea
                          id="typical_food"
                          value={quoteForm.destination.typical_food}
                          onChange={(e) => setQuoteForm({
                            ...quoteForm,
                            destination: {...quoteForm.destination, typical_food: e.target.value}
                          })}
                          placeholder="Pasta alla Carbonara, Supplì, Maritozzi..."
                          rows={2}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Services */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5" />
                        Servizi e Prezzi
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Add Service */}
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="service_name">Nome Servizio</Label>
                            <Input
                              id="service_name"
                              value={newService.name}
                              onChange={(e) => setNewService({...newService, name: e.target.value})}
                              placeholder="Volo A/R"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service_price">Prezzo (€)</Label>
                            <Input
                              id="service_price"
                              type="number"
                              step="0.01"
                              value={newService.price}
                              onChange={(e) => setNewService({...newService, price: e.target.value})}
                              placeholder="350.00"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service_category">Categoria</Label>
                            <Select value={newService.category} onValueChange={(value) => setNewService({...newService, category: value})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="transport">Trasporti</SelectItem>
                                <SelectItem value="accommodation">Alloggio</SelectItem>
                                <SelectItem value="excursions">Escursioni</SelectItem>
                                <SelectItem value="insurance">Assicurazione</SelectItem>
                                <SelectItem value="other">Altro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service_type">Tipo</Label>
                            <Select 
                              value={newService.is_included ? "included" : "supplement"} 
                              onValueChange={(value) => setNewService({...newService, is_included: value === "included"})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="included">Pacchetto Base</SelectItem>
                                <SelectItem value="supplement">Supplemento</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="service_description">Descrizione</Label>
                          <Input
                            id="service_description"
                            value={newService.description}
                            onChange={(e) => setNewService({...newService, description: e.target.value})}
                            placeholder="Volo diretto con bagaglio incluso"
                          />
                        </div>
                        <Button onClick={addService} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Servizio
                        </Button>
                      </div>

                      {/* Services List */}
                      <div className="space-y-3">
                        {quoteForm.services.map((service, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold">{service.name}</h4>
                                <Badge variant={service.is_included ? "default" : "secondary"}>
                                  {service.is_included ? "Pacchetto Base" : "Supplemento"}
                                </Badge>
                                <Badge variant="outline">{service.category}</Badge>
                              </div>
                              <p className="text-sm text-slate-600">{service.description}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-lg">{formatCurrency(service.price)}</span>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => removeService(index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pricing Summary */}
                      {quoteForm.services.length > 0 && (
                        <Card className="bg-slate-50">
                          <CardContent className="p-4">
                            <h4 className="font-semibold mb-3">Riepilogo Prezzi</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Pacchetto Base:</span>
                                <span className="font-medium">{formatCurrency(pricing.basePrice)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Supplementi:</span>
                                <span className="font-medium">{formatCurrency(pricing.supplementsPrice)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Subtotale:</span>
                                <span className="font-medium">{formatCurrency(pricing.totalPrice)}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Sconto (€):</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={quoteForm.discount_amount}
                                  onChange={(e) => setQuoteForm({...quoteForm, discount_amount: parseFloat(e.target.value) || 0})}
                                  className="w-24 h-8 text-right"
                                />
                              </div>
                              {quoteForm.discount_amount > 0 && (
                                <div className="flex justify-between text-red-600">
                                  <span>Sconto applicato:</span>
                                  <span className="font-medium">-{formatCurrency(pricing.discountAmount)}</span>
                                </div>
                              )}
                              <hr className="border-slate-300" />
                              <div className="flex justify-between text-lg font-bold">
                                <span>Totale finale:</span>
                                <span>{formatCurrency(pricing.finalPrice)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>

                  {/* Contacts */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Contatti
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="agent_name">Nome Agente</Label>
                          <Input
                            id="agent_name"
                            value={quoteForm.contacts.agent_name}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              contacts: {...quoteForm.contacts, agent_name: e.target.value}
                            })}
                            placeholder="Mario Bianchi"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="agent_email">Email Agente</Label>
                          <Input
                            id="agent_email"
                            type="email"
                            value={quoteForm.contacts.agent_email}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              contacts: {...quoteForm.contacts, agent_email: e.target.value}
                            })}
                            placeholder="mario@tribuviaggio.it"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="agent_whatsapp">WhatsApp Agente</Label>
                          <Input
                            id="agent_whatsapp"
                            value={quoteForm.contacts.agent_whatsapp}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              contacts: {...quoteForm.contacts, agent_whatsapp: e.target.value}
                            })}
                            placeholder="+39 333 123 4567"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Logo Agenzia (500x500 px consigliato)</Label>
                          <div className="flex items-center gap-4">
                            {/* Logo Preview */}
                            {quoteForm.agent_logo_url && (
                              <div className="relative">
                                <img 
                                  src={quoteForm.agent_logo_url.startsWith('http') ? quoteForm.agent_logo_url : `${BACKEND_URL}${quoteForm.agent_logo_url}`}
                                  alt="Logo" 
                                  className="w-20 h-20 object-contain border rounded-lg bg-white"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setQuoteForm({...quoteForm, agent_logo_url: ''})}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {/* Upload Button */}
                            <div className="flex-1">
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  
                                  if (file.size > 5 * 1024 * 1024) {
                                    toast.error('Il logo non può superare 5MB');
                                    return;
                                  }
                                  
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  
                                  try {
                                    const response = await axios.post(`${API}/upload-logo`, formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    setQuoteForm({...quoteForm, agent_logo_url: response.data.logo_url});
                                    toast.success('Logo caricato!');
                                  } catch (error) {
                                    toast.error('Errore nel caricamento del logo');
                                  }
                                }}
                                className="cursor-pointer"
                              />
                              <p className="text-xs text-slate-500 mt-1">PNG, JPG o WEBP (max 5MB)</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quote Content Sections */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Contenuto Preventivo
                      </CardTitle>
                      <CardDescription>
                        Personalizza la presentazione del preventivo al cliente
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="introduction">Introduzione</Label>
                        <textarea
                          id="introduction"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.introduction}
                          onChange={(e) => setQuoteForm({...quoteForm, introduction: e.target.value})}
                          placeholder="Gentile Cliente, è con grande piacere che Le presentiamo questa proposta di viaggio..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="travel_context">Contesto del Viaggio</Label>
                        <textarea
                          id="travel_context"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.travel_context}
                          onChange={(e) => setQuoteForm({...quoteForm, travel_context: e.target.value})}
                          placeholder="Questo viaggio nasce dalla vostra richiesta di..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="choice_explanation">Spiegazione della Scelta</Label>
                        <textarea
                          id="choice_explanation"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.choice_explanation}
                          onChange={(e) => setQuoteForm({...quoteForm, choice_explanation: e.target.value})}
                          placeholder="Abbiamo selezionato questa destinazione perché..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="client_benefits">Benefici per il Cliente</Label>
                        <textarea
                          id="client_benefits"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.client_benefits}
                          onChange={(e) => setQuoteForm({...quoteForm, client_benefits: e.target.value})}
                          placeholder="Con questa proposta avrete: assistenza 24/7, transfer inclusi, guide locali..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="ideal_for">Per Chi è Ideale</Label>
                        <textarea
                          id="ideal_for"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.ideal_for}
                          onChange={(e) => setQuoteForm({...quoteForm, ideal_for: e.target.value})}
                          placeholder="Questo viaggio è perfetto per famiglie con bambini, coppie in cerca di relax..."
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Image Gallery Upload */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Camera className="w-5 h-5" />
                        Gallery Fotografica
                      </CardTitle>
                      <CardDescription>
                        Carica fino a 5 immagini della destinazione (JPG, PNG)
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                        <Camera className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-slate-600">
                            Seleziona le immagini del viaggio
                          </p>
                          <p className="text-xs text-slate-500">
                            Massimo 5 file • JPG, PNG • Max 10MB ciascuno
                          </p>
                        </div>
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="mt-4"
                          disabled={uploadingImages}
                        />
                        {uploadingImages && (
                          <p className="text-sm text-blue-600 mt-2">
                            Caricamento in corso...
                          </p>
                        )}
                      </div>
                      
                      {/* Preview uploaded images */}
                      {quoteForm.gallery?.images?.length > 0 && (
                        <div className="space-y-2">
                          <Label>Immagini Caricate ({quoteForm.gallery.images.length}/5)</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {quoteForm.gallery.images.map((imageUrl, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`}
                                  alt={`Immagine ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border"
                                  onError={(e) => {
                                    e.target.src = `https://via.placeholder.com/200x100/e2e8f0/64748b?text=Immagine+${index+1}`;
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="absolute top-1 right-1 w-6 h-6 p-0"
                                  onClick={() => removeImage(index)}
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => {setShowCreateDialog(false); resetForm();}}>
                    Annulla
                  </Button>
                  <Button onClick={handleCreateQuote}>
                    Crea Preventivo
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {quotes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2">{quote.title}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {quote.client_registry ? 
                          `${quote.client_registry.first_name} ${quote.client_registry.last_name}` : 
                          quote.client_name || 'Cliente non specificato'
                        }
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      {quote.destination.flag_emoji}
                      <Badge variant="outline">{quote.destination.city}</Badge>
                      <Badge variant="secondary" className="ml-2">
                        {quote.travel_type || 'Mare Italia'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Prezzo finale:</span>
                        <div className="font-bold text-lg text-green-600">
                          {formatCurrency(quote.final_price)}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-600">Servizi:</span>
                        <div className="font-medium">{quote.services.length}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4" />
                      Creato: {new Date(quote.created_at).toLocaleDateString('it-IT')}
                    </div>

                    {quote.validity_date && (
                      <div className="flex items-center gap-2 text-sm">
                        {(() => {
                          const validityDate = new Date(quote.validity_date);
                          const today = new Date();
                          const isExpired = validityDate < today;
                          const daysRemaining = Math.ceil((validityDate - today) / (1000 * 60 * 60 * 24));
                          
                          return (
                            <div className={`flex items-center gap-1 ${
                              isExpired ? 'text-red-600' : daysRemaining <= 7 ? 'text-orange-600' : 'text-green-600'
                            }`}>
                              <Clock className="w-4 h-4" />
                              {isExpired ? (
                                <span>Scaduto il {validityDate.toLocaleDateString('it-IT')}</span>
                              ) : (
                                <span>Valido fino al {validityDate.toLocaleDateString('it-IT')}</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Eye className="w-4 h-4" />
                      {quote.views_count} visualizzazioni
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleCopyUrl(quote)}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copia URL
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(`/preventivo/${quote.public_url_slug}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEditQuote(quote)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDeleteQuote(quote.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-16">
              <FileText className="mx-auto h-16 w-16 text-slate-400 mb-6" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                Nessun preventivo creato
              </h3>
              <p className="text-slate-600 mb-6">
                Crea il tuo primo preventivo professionale
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crea il primo preventivo
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit Dialog - Complete form like create dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Preventivo</DialogTitle>
            <DialogDescription>
              Modifica tutti i dettagli del preventivo
            </DialogDescription>
          </DialogHeader>
          
                <div className="space-y-6">
                  {/* Basic Info - Updated for travel type and validity */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Titolo Preventivo *</Label>
                      <Input
                        id="title"
                        value={quoteForm.title}
                        onChange={(e) => setQuoteForm({...quoteForm, title: e.target.value})}
                        placeholder="es. Vacanza Costiera Amalfitana"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="discount_amount">Sconto (€)</Label>
                      <Input
                        id="discount_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={quoteForm.discount_amount}
                        onChange={(e) => setQuoteForm({...quoteForm, discount_amount: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="validity_date">Data Validità Preventivo</Label>
                      <Input
                        id="validity_date"
                        type="date"
                        value={quoteForm.validity_date}
                        onChange={(e) => setQuoteForm({...quoteForm, validity_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-slate-500">
                        Fino a quando il preventivo è valido
                      </p>
                    </div>
                  </div>

                  {/* Tour Operator from Rubrica (optional link) */}
                  <TourOperatorSelect
                    value={quoteForm.tour_operator_id}
                    onChange={(id) => setQuoteForm({...quoteForm, tour_operator_id: id})}
                    label="Tour Operator collegato (dalla Rubrica T.O.) — opzionale"
                    placeholder="Collega un T.O. al preventivo (facoltativo)"
                    testId="quote-tour-operator-select"
                  />

                  {/* Travel Type Selector */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Tipo di Viaggio / Pacchetto
                      </CardTitle>
                      <CardDescription>
                        Seleziona la tipologia di viaggio per personalizzare l'offerta
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label htmlFor="travel_type">Tipo di Pacchetto *</Label>
                        <Select 
                          value={quoteForm.travel_type} 
                          onValueChange={(value) => {
                            setQuoteForm({
                              ...quoteForm, 
                              travel_type: value,
                              // Reset itinerary if changing to/from types that don't require it
                              itinerary: requiresItinerary(value) ? quoteForm.itinerary : {
                                days: [],
                                total_days: 0,
                                notes: ''
                              }
                            });
                            // Reset new itinerary day
                            setNewItineraryDay({
                              day_number: 1,
                              location: '',
                              activities: '',
                              meals: '',
                              accommodation: ''
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona tipo di viaggio" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Crociera">🚢 Crociera</SelectItem>
                            <SelectItem value="Mare Italia">🏖️ Mare Italia</SelectItem>
                            <SelectItem value="Montagna inverno">⛷️ Montagna Inverno</SelectItem>
                            <SelectItem value="Montagna estate">🏔️ Montagna Estate</SelectItem>
                            <SelectItem value="Medio Raggio">✈️ Medio Raggio</SelectItem>
                            <SelectItem value="Lungo Raggio">🌍 Lungo Raggio</SelectItem>
                            <SelectItem value="Gruppi Organizzati">👥 Gruppi Organizzati</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <strong>Tipo selezionato:</strong> {quoteForm.travel_type}
                          {requiresItinerary(quoteForm.travel_type) && (
                            <span className="ml-2 text-blue-600">• Include sezione itinerario</span>
                          )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Travel Dates Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Date del Viaggio
                      </CardTitle>
                      <CardDescription>
                        Periodo e durata del viaggio
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="start_date">Data Partenza *</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={quoteForm.start_date}
                            onChange={(e) => {
                              const startDate = e.target.value;
                              const endDate = quoteForm.end_date;
                              let nights = quoteForm.nights;
                              
                              // Auto-calculate nights if both dates are set
                              if (startDate && endDate) {
                                const start = new Date(startDate);
                                const end = new Date(endDate);
                                const diffTime = end - start;
                                nights = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
                              }
                              
                              setQuoteForm({
                                ...quoteForm,
                                start_date: startDate,
                                nights: nights
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="end_date">Data Rientro *</Label>
                          <Input
                            id="end_date"
                            type="date"
                            value={quoteForm.end_date}
                            min={quoteForm.start_date}
                            onChange={(e) => {
                              const endDate = e.target.value;
                              const startDate = quoteForm.start_date;
                              let nights = quoteForm.nights;
                              
                              // Auto-calculate nights if both dates are set
                              if (startDate && endDate) {
                                const start = new Date(startDate);
                                const end = new Date(endDate);
                                const diffTime = end - start;
                                nights = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
                              }
                              
                              setQuoteForm({
                                ...quoteForm,
                                end_date: endDate,
                                nights: nights
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nights">Notti</Label>
                          <Input
                            id="nights"
                            type="number"
                            min="0"
                            value={quoteForm.nights}
                            onChange={(e) => setQuoteForm({...quoteForm, nights: parseInt(e.target.value) || 0})}
                            className="bg-slate-50"
                          />
                          <p className="text-xs text-slate-500">Calcolato automaticamente</p>
                        </div>
                      </div>
                      
                      {/* Summary */}
                      {quoteForm.start_date && quoteForm.end_date && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-700">
                            📅 <strong>Periodo:</strong> {new Date(quoteForm.start_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })} - {new Date(quoteForm.end_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                            <span className="ml-3">🌙 <strong>{quoteForm.nights}</strong> notti</span>
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Itinerary Section - Conditional */}
                  {requiresItinerary(quoteForm.travel_type) && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Itinerario di Viaggio
                        </CardTitle>
                        <CardDescription>
                          Programma dettagliato per {quoteForm.travel_type}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Add Day Form */}
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 space-y-4">
                          <h4 className="font-medium">Aggiungi Giorno all'Itinerario</h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="day_location">Località *</Label>
                              <Input
                                id="day_location"
                                value={newItineraryDay.location}
                                onChange={(e) => setNewItineraryDay({...newItineraryDay, location: e.target.value})}
                                placeholder="es. Roma, Barcellona, Santorini"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="day_accommodation">Sistemazione</Label>
                              <Input
                                id="day_accommodation"
                                value={newItineraryDay.accommodation}
                                onChange={(e) => setNewItineraryDay({...newItineraryDay, accommodation: e.target.value})}
                                placeholder="es. Hotel 4*, Nave, Pensione"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="day_activities">Attività Programmate *</Label>
                            <Textarea
                              id="day_activities"
                              value={newItineraryDay.activities}
                              onChange={(e) => setNewItineraryDay({...newItineraryDay, activities: e.target.value})}
                              placeholder="Descrivi le attività, visite, escursioni previste"
                              rows={3}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="day_meals">Pasti Inclusi</Label>
                            <Input
                              id="day_meals"
                              value={newItineraryDay.meals}
                              onChange={(e) => setNewItineraryDay({...newItineraryDay, meals: e.target.value})}
                              placeholder="es. Colazione, Pranzo, Cena"
                            />
                          </div>
                          
                          <Button onClick={addItineraryDay} className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            Aggiungi Giorno {newItineraryDay.day_number}
                          </Button>
                        </div>

                        {/* Itinerary Days List */}
                        <div className="space-y-3">
                          {quoteForm.itinerary.days.map((day, index) => (
                            <div key={index} className="p-4 border rounded-lg bg-white">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline">Giorno {day.day_number}</Badge>
                                    <h4 className="font-semibold">{day.location}</h4>
                                  </div>
                                  <div className="space-y-1 text-sm text-slate-600">
                                    <p><strong>Attività:</strong> {day.activities}</p>
                                    {day.accommodation && (
                                      <p><strong>Sistemazione:</strong> {day.accommodation}</p>
                                    )}
                                    {day.meals && (
                                      <p><strong>Pasti:</strong> {day.meals}</p>
                                    )}
                                  </div>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => removeItineraryDay(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          
                          {quoteForm.itinerary.days.length === 0 && (
                            <div className="text-center py-8 text-slate-500">
                              <Calendar className="mx-auto h-12 w-12 text-slate-300 mb-3" />
                              <p>Nessun giorno aggiunto all'itinerario</p>
                              <p className="text-sm">Aggiungi i giorni del programma di viaggio</p>
                            </div>
                          )}
                        </div>

                        {/* Itinerary Notes */}
                        <div className="space-y-2">
                          <Label htmlFor="itinerary_notes">Note Itinerario</Label>
                          <Textarea
                            id="itinerary_notes"
                            value={quoteForm.itinerary.notes}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              itinerary: {...quoteForm.itinerary, notes: e.target.value}
                            })}
                            placeholder="Note generali, informazioni importanti, variazioni possibili..."
                            rows={2}
                          />
                        </div>

                        {/* Itinerary Summary */}
                        {quoteForm.itinerary.days.length > 0 && (
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <h4 className="font-medium text-blue-800 mb-1">Riepilogo Itinerario</h4>
                            <p className="text-sm text-blue-700">
                              Programma di {quoteForm.itinerary.total_days} giorni • 
                              Località visitate: {quoteForm.itinerary.days.map(d => d.location).join(', ')}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Client Registry - Complete client information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Anagrafica Cliente
                      </CardTitle>
                      <CardDescription>
                        Informazioni complete del cliente intestatario
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_first_name">Nome *</Label>
                          <Input
                            id="client_first_name"
                            value={quoteForm.client_registry.first_name}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, first_name: e.target.value}
                            })}
                            placeholder="Mario"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_last_name">Cognome *</Label>
                          <Input
                            id="client_last_name"
                            value={quoteForm.client_registry.last_name}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, last_name: e.target.value}
                            })}
                            placeholder="Rossi"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_email">Email *</Label>
                          <Input
                            id="client_email"
                            type="email"
                            value={quoteForm.client_registry.email}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, email: e.target.value}
                            })}
                            placeholder="mario.rossi@email.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_phone">Telefono *</Label>
                          <Input
                            id="client_phone"
                            value={quoteForm.client_registry.phone}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, phone: e.target.value}
                            })}
                            placeholder="+39 333 123 4567"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_birth_date">Data di Nascita</Label>
                          <Input
                            id="client_birth_date"
                            type="date"
                            value={quoteForm.client_registry.date_of_birth}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, date_of_birth: e.target.value}
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_nationality">Nazionalità</Label>
                          <Input
                            id="client_nationality"
                            value={quoteForm.client_registry.nationality}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, nationality: e.target.value}
                            })}
                            placeholder="Italiana"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_tax_code">Codice Fiscale</Label>
                          <Input
                            id="client_tax_code"
                            value={quoteForm.client_registry.tax_code}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, tax_code: e.target.value}
                            })}
                            placeholder="RSSMRA80A01H501X"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client_address">Indirizzo Completo</Label>
                        <Textarea
                          id="client_address"
                          value={quoteForm.client_registry.address}
                          onChange={(e) => setQuoteForm({
                            ...quoteForm,
                            client_registry: {...quoteForm.client_registry, address: e.target.value}
                          })}
                          placeholder="Via Roma 123, 20100 Milano (MI)"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_city">Città</Label>
                          <Input
                            id="client_city"
                            value={quoteForm.client_registry.city}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, city: e.target.value}
                            })}
                            placeholder="Milano"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_postal_code">CAP</Label>
                          <Input
                            id="client_postal_code"
                            value={quoteForm.client_registry.postal_code}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {...quoteForm.client_registry, postal_code: e.target.value}
                            })}
                            placeholder="20100"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Participants Management - Simplified */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Partecipanti al Viaggio
                      </CardTitle>
                      <CardDescription>
                        Indica il numero di adulti e bambini
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="adults_count">Adulti (18+ anni)</Label>
                          <Input
                            id="adults_count"
                            type="number"
                            min="1"
                            max="10"
                            value={quoteForm.client_registry.participants.adults}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              client_registry: {
                                ...quoteForm.client_registry,
                                participants: {
                                  ...quoteForm.client_registry.participants,
                                  adults: parseInt(e.target.value) || 1
                                }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="children_count">Bambini (1-17 anni)</Label>
                          <Input
                            id="children_count"
                            type="number"
                            min="0"
                            max="6"
                            value={quoteForm.client_registry.participants.children}
                            onChange={(e) => updateChildrenAges(parseInt(e.target.value) || 0)}
                          />
                        </div>
                      </div>

                      {/* Children Ages */}
                      {quoteForm.client_registry.participants.children > 0 && (
                        <div className="space-y-3">
                          <Label>Età dei Bambini</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Array.from({ length: quoteForm.client_registry.participants.children }, (_, index) => (
                              <div key={index} className="space-y-1">
                                <Label className="text-sm">Bambino {index + 1}</Label>
                                <Select 
                                  value={String(quoteForm.client_registry.participants.children_ages[index] || 5)}
                                  onValueChange={(value) => updateChildAge(index, value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: 17 }, (_, i) => i + 1).map((age) => (
                                      <SelectItem key={age} value={String(age)}>
                                        {age} {age === 1 ? 'anno' : 'anni'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <h4 className="font-medium text-slate-800 mb-2">Riepilogo Partecipanti</h4>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span>{quoteForm.client_registry.participants.adults} Adulti</span>
                          {quoteForm.client_registry.participants.children > 0 && (
                            <span>
                              {quoteForm.client_registry.participants.children} Bambini
                              {quoteForm.client_registry.participants.children_ages.length > 0 && (
                                <span className="ml-1">
                                  (età: {quoteForm.client_registry.participants.children_ages.join(', ')})
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 text-sm font-medium text-slate-800">
                          Totale: {quoteForm.client_registry.participants.adults + quoteForm.client_registry.participants.children} persone
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Destination */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Informazioni Destinazione
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="country">Paese *</Label>
                          <Input
                            id="country"
                            value={quoteForm.destination.country}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, country: e.target.value}
                            })}
                            placeholder="Italia"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city">Città *</Label>
                          <Input
                            id="city"
                            value={quoteForm.destination.city}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, city: e.target.value}
                            })}
                            placeholder="Roma"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="flag_emoji">Bandiera Emoji</Label>
                          <Input
                            id="flag_emoji"
                            value={quoteForm.destination.flag_emoji}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, flag_emoji: e.target.value}
                            })}
                            placeholder="🇮🇹"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="climate">Clima</Label>
                          <Input
                            id="climate"
                            value={quoteForm.destination.climate}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, climate: e.target.value}
                            })}
                            placeholder="Mediterraneo"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="best_period">Periodo Migliore</Label>
                          <Input
                            id="best_period"
                            value={quoteForm.destination.best_period}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              destination: {...quoteForm.destination, best_period: e.target.value}
                            })}
                            placeholder="Maggio-Settembre"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="attractions">Attrazioni Principali</Label>
                        <Textarea
                          id="attractions"
                          value={quoteForm.destination.attractions}
                          onChange={(e) => setQuoteForm({
                            ...quoteForm,
                            destination: {...quoteForm.destination, attractions: e.target.value}
                          })}
                          placeholder="Colosseo, Fontana di Trevi, Musei Vaticani..."
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="typical_food">Cucina Tipica</Label>
                        <Textarea
                          id="typical_food"
                          value={quoteForm.destination.typical_food}
                          onChange={(e) => setQuoteForm({
                            ...quoteForm,
                            destination: {...quoteForm.destination, typical_food: e.target.value}
                          })}
                          placeholder="Pasta alla Carbonara, Supplì, Maritozzi..."
                          rows={2}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Services */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5" />
                        Servizi e Prezzi
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Add Service */}
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="service_name">Nome Servizio</Label>
                            <Input
                              id="service_name"
                              value={newService.name}
                              onChange={(e) => setNewService({...newService, name: e.target.value})}
                              placeholder="Volo A/R"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service_price">Prezzo (€)</Label>
                            <Input
                              id="service_price"
                              type="number"
                              step="0.01"
                              value={newService.price}
                              onChange={(e) => setNewService({...newService, price: e.target.value})}
                              placeholder="350.00"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service_category">Categoria</Label>
                            <Select value={newService.category} onValueChange={(value) => setNewService({...newService, category: value})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="transport">Trasporti</SelectItem>
                                <SelectItem value="accommodation">Alloggio</SelectItem>
                                <SelectItem value="excursions">Escursioni</SelectItem>
                                <SelectItem value="insurance">Assicurazione</SelectItem>
                                <SelectItem value="other">Altro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service_type">Tipo</Label>
                            <Select 
                              value={newService.is_included ? "included" : "supplement"} 
                              onValueChange={(value) => setNewService({...newService, is_included: value === "included"})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="included">Pacchetto Base</SelectItem>
                                <SelectItem value="supplement">Supplemento</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="service_description">Descrizione</Label>
                          <Input
                            id="service_description"
                            value={newService.description}
                            onChange={(e) => setNewService({...newService, description: e.target.value})}
                            placeholder="Volo diretto con bagaglio incluso"
                          />
                        </div>
                        <Button onClick={addService} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Servizio
                        </Button>
                      </div>

                      {/* Services List */}
                      <div className="space-y-3">
                        {quoteForm.services.map((service, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold">{service.name}</h4>
                                <Badge variant={service.is_included ? "default" : "secondary"}>
                                  {service.is_included ? "Pacchetto Base" : "Supplemento"}
                                </Badge>
                                <Badge variant="outline">{service.category}</Badge>
                              </div>
                              <p className="text-sm text-slate-600">{service.description}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-lg">{formatCurrency(service.price)}</span>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => removeService(index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pricing Summary */}
                      {quoteForm.services.length > 0 && (
                        <Card className="bg-slate-50">
                          <CardContent className="p-4">
                            <h4 className="font-semibold mb-3">Riepilogo Prezzi</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Pacchetto Base:</span>
                                <span className="font-medium">{formatCurrency(pricing.basePrice)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Supplementi:</span>
                                <span className="font-medium">{formatCurrency(pricing.supplementsPrice)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Subtotale:</span>
                                <span className="font-medium">{formatCurrency(pricing.totalPrice)}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Sconto (€):</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={quoteForm.discount_amount}
                                  onChange={(e) => setQuoteForm({...quoteForm, discount_amount: parseFloat(e.target.value) || 0})}
                                  className="w-24 h-8 text-right"
                                />
                              </div>
                              {quoteForm.discount_amount > 0 && (
                                <div className="flex justify-between text-red-600">
                                  <span>Sconto applicato:</span>
                                  <span className="font-medium">-{formatCurrency(pricing.discountAmount)}</span>
                                </div>
                              )}
                              <hr className="border-slate-300" />
                              <div className="flex justify-between text-lg font-bold">
                                <span>Totale finale:</span>
                                <span>{formatCurrency(pricing.finalPrice)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>

                  {/* Contacts */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Contatti
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="agent_name">Nome Agente</Label>
                          <Input
                            id="agent_name"
                            value={quoteForm.contacts.agent_name}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              contacts: {...quoteForm.contacts, agent_name: e.target.value}
                            })}
                            placeholder="Mario Bianchi"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="agent_email">Email Agente</Label>
                          <Input
                            id="agent_email"
                            type="email"
                            value={quoteForm.contacts.agent_email}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              contacts: {...quoteForm.contacts, agent_email: e.target.value}
                            })}
                            placeholder="mario@tribuviaggio.it"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="agent_whatsapp">WhatsApp Agente</Label>
                          <Input
                            id="agent_whatsapp"
                            value={quoteForm.contacts.agent_whatsapp}
                            onChange={(e) => setQuoteForm({
                              ...quoteForm,
                              contacts: {...quoteForm.contacts, agent_whatsapp: e.target.value}
                            })}
                            placeholder="+39 333 123 4567"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Logo Agenzia (500x500 px consigliato)</Label>
                          <div className="flex items-center gap-4">
                            {/* Logo Preview */}
                            {quoteForm.agent_logo_url && (
                              <div className="relative">
                                <img 
                                  src={quoteForm.agent_logo_url.startsWith('http') ? quoteForm.agent_logo_url : `${BACKEND_URL}${quoteForm.agent_logo_url}`}
                                  alt="Logo" 
                                  className="w-20 h-20 object-contain border rounded-lg bg-white"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setQuoteForm({...quoteForm, agent_logo_url: ''})}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {/* Upload Button */}
                            <div className="flex-1">
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  
                                  if (file.size > 5 * 1024 * 1024) {
                                    toast.error('Il logo non può superare 5MB');
                                    return;
                                  }
                                  
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  
                                  try {
                                    const response = await axios.post(`${API}/upload-logo`, formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    setQuoteForm({...quoteForm, agent_logo_url: response.data.logo_url});
                                    toast.success('Logo caricato!');
                                  } catch (error) {
                                    toast.error('Errore nel caricamento del logo');
                                  }
                                }}
                                className="cursor-pointer"
                              />
                              <p className="text-xs text-slate-500 mt-1">PNG, JPG o WEBP (max 5MB)</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quote Content Sections */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Contenuto Preventivo
                      </CardTitle>
                      <CardDescription>
                        Personalizza la presentazione del preventivo al cliente
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="introduction">Introduzione</Label>
                        <textarea
                          id="introduction"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.introduction}
                          onChange={(e) => setQuoteForm({...quoteForm, introduction: e.target.value})}
                          placeholder="Gentile Cliente, è con grande piacere che Le presentiamo questa proposta di viaggio..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="travel_context">Contesto del Viaggio</Label>
                        <textarea
                          id="travel_context"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.travel_context}
                          onChange={(e) => setQuoteForm({...quoteForm, travel_context: e.target.value})}
                          placeholder="Questo viaggio nasce dalla vostra richiesta di..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="choice_explanation">Spiegazione della Scelta</Label>
                        <textarea
                          id="choice_explanation"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.choice_explanation}
                          onChange={(e) => setQuoteForm({...quoteForm, choice_explanation: e.target.value})}
                          placeholder="Abbiamo selezionato questa destinazione perché..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="client_benefits">Benefici per il Cliente</Label>
                        <textarea
                          id="client_benefits"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.client_benefits}
                          onChange={(e) => setQuoteForm({...quoteForm, client_benefits: e.target.value})}
                          placeholder="Con questa proposta avrete: assistenza 24/7, transfer inclusi, guide locali..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="ideal_for">Per Chi è Ideale</Label>
                        <textarea
                          id="ideal_for"
                          className="w-full min-h-[80px] p-3 border rounded-lg resize-y text-sm"
                          value={quoteForm.ideal_for}
                          onChange={(e) => setQuoteForm({...quoteForm, ideal_for: e.target.value})}
                          placeholder="Questo viaggio è perfetto per famiglie con bambini, coppie in cerca di relax..."
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Image Gallery Upload */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Camera className="w-5 h-5" />
                        Gallery Fotografica
                      </CardTitle>
                      <CardDescription>
                        Carica fino a 5 immagini della destinazione (JPG, PNG)
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                        <Camera className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-slate-600">
                            Seleziona le immagini del viaggio
                          </p>
                          <p className="text-xs text-slate-500">
                            Massimo 5 file • JPG, PNG • Max 10MB ciascuno
                          </p>
                        </div>
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="mt-4"
                          disabled={uploadingImages}
                        />
                        {uploadingImages && (
                          <p className="text-sm text-blue-600 mt-2">
                            Caricamento in corso...
                          </p>
                        )}
                      </div>
                      
                      {/* Preview uploaded images */}
                      {quoteForm.gallery?.images?.length > 0 && (
                        <div className="space-y-2">
                          <Label>Immagini Caricate ({quoteForm.gallery.images.length}/5)</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {quoteForm.gallery.images.map((imageUrl, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`}
                                  alt={`Immagine ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border"
                                  onError={(e) => {
                                    e.target.src = `https://via.placeholder.com/200x100/e2e8f0/64748b?text=Immagine+${index+1}`;
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="absolute top-1 right-1 w-6 h-6 p-0"
                                  onClick={() => removeImage(index)}
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => {setShowEditDialog(false); setEditingQuote(null); resetForm();}}>
                    Annulla
                  </Button>
                  <Button onClick={handleUpdateQuote}>
                    Salva Modifiche
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
    </div>
  );
};

export default QuoteManager;